import { useState, useEffect, useRef } from 'react';
import { getAvatarUrl } from '../../utils/avatar';
import { User, Mail, Phone, Lock } from 'lucide-react';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { useConfirmation } from '../../context/ConfirmationContext';
import Toast from '../../components/Toast';

export default function StudentProfile() {
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwords, setPasswords] = useState({ current: '', new: '', confirm: '' });
  const [passwordError, setPasswordError] = useState('');
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState('success');

  // Inline descriptive message for photo actions (no toast)
  const [photoMessage, setPhotoMessage] = useState('');
  const [photoMessageType, setPhotoMessageType] = useState('success');

  const { showConfirmation } = useConfirmation();

  // Avatar upload state
  const [preview, setPreview] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const fileInputRef = useRef(null);
  const [showPhotoOptions, setShowPhotoOptions] = useState(false);

  const handleAvatarChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const allowed = ['image/jpeg','image/png','image/webp'];
    if (!allowed.includes(file.type)) {
      setPhotoMessage('Unsupported file type. Allowed: JPEG, PNG, WEBP');
      setPhotoMessageType('error');
      setTimeout(() => setPhotoMessage(''), 4000);
      return;
    }
    if (file.size > 3 * 1024 * 1024) {
      setPhotoMessage('File too large (max 3MB)');
      setPhotoMessageType('error');
      setTimeout(() => setPhotoMessage(''), 4000);
      return;
    }

    // Set preview and selected file
    setSelectedFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setPreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const uploadAvatar = async () => {
    if (!selectedFile) return;
    const formData = new FormData();
    formData.append('avatar', selectedFile);
    try {
      const res = await api.post('/student/profile/avatar', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      if (res.data.success) {
        setPhotoMessage('Avatar uploaded successfully');
        setPhotoMessageType('success');
        setTimeout(() => setPhotoMessage(''), 4000);
        setPreview(null);
        setSelectedFile(null);
        // Refresh profile
        loadProfile();
        // Notify auth context to refresh (header avatar)
        window.dispatchEvent(new Event('lit:auth-refresh'));
      }
    } catch (err) {
      setPhotoMessage(err.response?.data?.message || 'Failed to upload avatar');
      setPhotoMessageType('error');
      setTimeout(() => setPhotoMessage(''), 4000);
    }
  };

  const removeAvatar = async () => {
    try {
      const res = await api.delete('/student/profile/avatar');
      if (res.data.success) {
        setPhotoMessage('Avatar removed');
        setPhotoMessageType('success');
        setTimeout(() => setPhotoMessage(''), 4000);
        // Refresh profile
        loadProfile();
        // Notify auth context to refresh (header avatar)
        window.dispatchEvent(new Event('lit:auth-refresh'));
      }
    } catch (err) {
      setPhotoMessage(err.response?.data?.message || 'Failed to remove avatar');
      setPhotoMessageType('error');
      setTimeout(() => setPhotoMessage(''), 4000);
    }
  };

  useEffect(() => { loadProfile(); }, []);

  const loadProfile = async () => {
    try {
      const res = await api.get('/student/profile');
      if (res.data.success && res.data.data) {
        setProfile(res.data.data.profile);
      }
    } catch (err) {
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = (e) => {
    e.preventDefault();
    setPasswordError('');

    if (passwords.new !== passwords.confirm) {
      setPasswordError('New passwords do not match');
      return;
    }
    if (passwords.new.length < 8) {
      setPasswordError('Password must be at least 8 characters');
      return;
    }

    // Use global confirmation popup
    showConfirmation({
      title: 'Confirm password change',
      message: 'Are you sure you want to change your password?',
      confirmText: 'Change',
      cancelText: 'Cancel',
      type: 'info',
      onConfirm: () => performPasswordChange()
    });
  }; 

  const performPasswordChange = async () => {
    try {
      const res = await api.put('/student/password', {
        currentPassword: passwords.current,
        newPassword: passwords.new
      });

      if (res.data.success) {
        setPasswords({ current: '', new: '', confirm: '' });
        setShowPasswordModal(false);
        setToastMessage('Password changed successfully!');
        setToastType('success');
        setShowToast(true);
        setTimeout(() => setShowToast(false), 4000);
      }
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to change password';
      setToastMessage(msg);
      setToastType('error');
      setShowToast(true);
      setTimeout(() => setShowToast(false), 4000);
    }
  }; 

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div></div>;
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-800">My Profile</h1>
        <p className="text-gray-600">View your personal information</p>
      </div>

      {/* Profile Card */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        {/* Header Banner */}
        <div className="h-32 bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500"></div>
        
        {/* Profile Info */}
        <div className="px-6 pb-6">
          <div className="flex flex-col items-center -mt-16">
            <div className="w-32 h-32 rounded-full bg-white border-4 border-white shadow-lg flex items-center justify-center relative">
              {profile?.avatar_path ? (
                <button onClick={() => setShowPhotoOptions(true)} className="w-32 h-32 rounded-full overflow-hidden p-0 m-0 block cursor-pointer" title="Manage photo">
                  <img src={getAvatarUrl(profile.avatar_path)} alt="avatar" className="w-32 h-32 rounded-full object-cover" />
                </button>
              ) : (
                <button onClick={() => setShowPhotoOptions(true)} className="w-32 h-32 rounded-full bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center cursor-pointer" title="Manage photo">
                  <span className="text-white font-bold text-4xl">{profile?.full_name?.charAt(0) || 'S'}</span>
                </button>
              )}

              {/* Photo options modal */}
              {showPhotoOptions && (
                <div className="fixed inset-0 z-50 flex items-center justify-center">
                  <div className="absolute inset-0 bg-black/40" onClick={() => setShowPhotoOptions(false)}></div>
                  <div className="relative bg-white rounded-lg shadow-md w-full max-w-sm mx-4 p-5">
                    <h3 className="text-lg font-semibold text-gray-800 mb-3">Profile photo</h3>
                    <p className="text-sm text-gray-600 mb-4">Choose an action. Deleting your photo will open a confirmation dialog.</p>

                    <div className="flex flex-col gap-3">
                      <button onClick={() => { setShowPhotoOptions(false); fileInputRef.current?.click(); }} className="w-full px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700">{profile?.avatar_path ? 'Update Photo' : 'Add Photo'}</button>

                      {profile?.avatar_path && (
                        <button onClick={() => { 
                          setShowPhotoOptions(false); 
                          showConfirmation({
                            title: 'Delete profile photo',
                            message: 'Are you sure you want to delete your profile photo? This action cannot be undone.',
                            confirmText: 'Delete',
                            cancelText: 'Cancel',
                            type: 'danger',
                            onConfirm: () => removeAvatar()
                          });
                        }} className="w-full px-4 py-2 bg-red-50 text-red-700 rounded-lg border border-red-100 hover:bg-red-100">Delete Photo</button>
                      )} 

                      <button onClick={() => setShowPhotoOptions(false)} className="w-full px-4 py-2 bg-gray-100 rounded-lg">Cancel</button>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="mt-3">
              {/* Hidden file input - triggered from the Photo options modal */}
              <input id="avatarInput" ref={fileInputRef} type="file" accept="image/*" onChange={handleAvatarChange} className="hidden" />
            </div>

            {preview && (
              <div className="mt-3">
                <img src={preview} alt="preview" className="w-24 h-24 rounded-full object-cover border" />
                <div className="mt-2 flex gap-2">
                  <button onClick={() => uploadAvatar()} className="px-3 py-1 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700">Save</button>
                  <button onClick={() => setPreview(null)} className="px-3 py-1 bg-gray-100 rounded-lg">Cancel</button>
                </div>
                {photoMessage && (
                  <p className={`mt-2 text-sm ${photoMessageType === 'success' ? 'text-green-600' : 'text-red-600'}`}>{photoMessage}</p>
                )}
              </div>
            )}

            <h2 className="mt-4 text-2xl font-bold text-gray-800">{profile?.full_name}</h2>
            <span className="mt-1 px-4 py-1 bg-indigo-100 text-indigo-600 rounded-full text-sm font-medium">Student</span>
          </div>

          {/* Details */}
          <div className="mt-8 space-y-4">
            <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl">
              <div className="p-3 bg-indigo-100 rounded-xl">
                <User className="w-5 h-5 text-indigo-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Full Name</p>
                <p className="font-medium text-gray-800">{profile?.full_name || 'Not set'}</p>
              </div>
            </div>

            <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl">
              <div className="p-3 bg-blue-100 rounded-xl">
                <Mail className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Email Address</p>
                <p className="font-medium text-gray-800">{profile?.email || 'Not set'}</p>
              </div>
            </div>

            <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl">
              <div className="p-3 bg-green-100 rounded-xl">
                <Phone className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Phone Number</p>
                <p className="font-medium text-gray-800">{profile?.phone_number && profile.phone_number !== 'null' ? profile.phone_number : 'Not set'}</p>
              </div>
            </div>
          </div>

          {/* Note */}
          <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-xl">
            <p className="text-sm text-amber-700">
              <strong>Note:</strong> To update your profile information, please contact the school administrator.
            </p>
          </div>

          {/* Change Password Action */}
          <div className="mt-6">
            <button onClick={() => setShowPasswordModal(true)} className="w-full px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 flex items-center justify-center gap-2 shadow-sm">
              <Lock className="w-4 h-4" /> Change Password
            </button>
          </div>
        </div>
      </div>

      {/* Toast */}
      <div className="fixed top-4 right-4 w-full max-w-sm z-50">
        <Toast message={toastMessage} type={toastType} visible={showToast} onClose={() => setShowToast(false)} />
      </div>

      {/* Change Password Modal */}
      {showPasswordModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={() => setShowPasswordModal(false)}></div>
          <div className="relative bg-white rounded-lg shadow-md w-full max-w-md mx-4 p-5">
            <h2 className="text-lg font-semibold text-gray-800 mb-3">Change Password</h2>

            {passwordError && <div className="mb-3 text-sm text-red-600">{passwordError}</div>}

            <form onSubmit={handlePasswordChange} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Current Password</label>
                <input type="password" required value={passwords.current} onChange={(e) => setPasswords({...passwords, current: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">New Password</label>
                <input
                  type="password"
                  required
                  value={passwords.new}
                  onChange={(e) => setPasswords({...passwords, new: e.target.value})}
                  className={`w-full px-3 py-2 rounded-lg focus:ring-2 focus:ring-indigo-500 ${passwords.new.length>0 && passwords.new.length<8 ? 'border border-red-300' : passwords.new.length>=8 ? 'border border-green-300' : 'border border-gray-300'}`} />
                {passwords.new.length>0 && passwords.new.length<8 && <p className="text-sm text-red-600 mt-1">Must be at least 8 characters</p>}
                {passwords.new.length>=8 && <p className="text-sm text-green-600 mt-1">Password length OK</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Confirm New Password</label>
                <input
                  type="password"
                  required
                  value={passwords.confirm}
                  onChange={(e) => setPasswords({...passwords, confirm: e.target.value})}
                  className={`w-full px-3 py-2 rounded-lg focus:ring-2 focus:ring-indigo-500 ${passwords.confirm.length>0 && passwords.confirm!==passwords.new ? 'border border-red-300' : passwords.confirm.length>0 && passwords.confirm===passwords.new && passwords.new.length>=8 ? 'border border-green-300' : 'border border-gray-300'}`} />
                {passwords.confirm.length>0 && passwords.confirm!==passwords.new && <p className="text-sm text-red-600 mt-1">Passwords do not match</p>}
                {passwords.confirm.length>0 && passwords.confirm===passwords.new && passwords.new.length>=8 && <p className="text-sm text-green-600 mt-1">Passwords match</p>}
              </div>
              <div className="flex gap-3 pt-3">
                <button type="button" onClick={() => setShowPasswordModal(false)} className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50">Cancel</button>
                <button type="submit" className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700">Change Password</button>
              </div>
            </form>
          </div>
        </div>
      )}


    </div>
  );
}
